/**
 * Lambda Cart API Service
 *
 * Browser-safe service that calls the Lambda Cart API
 * Replaces direct DynamoDB access with HTTP API calls
 */

import { logger } from '@/utils/logger';

export interface LambdaCartSession {
  id: string;
  user_id: string;
  created_at: string;
  expires_at: string;
  metadata?: Record<string, any>;
}

export interface LambdaCartItem {
  id: string;
  session_id: string;
  source_type: 'quote' | 'product' | 'consolidation';
  source_id: string;
  quantity: number;
  original_amount: number;
  currency: string;
  discounted_amount?: number;
  addon_cost?: number;
  selected_addon_services?: any[];
  applied_discounts?: any[];
  selected_options?: Record<string, any>;
  catalog_variant_id?: string;
  created_at: string;
  updated_at: string;
}

export class LambdaCartApiService {
  private static instance: LambdaCartApiService;
  private baseUrl: string;

  private constructor() {
    // Use environment variable or fallback to development URL
    this.baseUrl = import.meta.env.VITE_CART_API_BASE_URL || 'https://api-gateway-not-deployed-yet.com/prod';

    // Development mode check
    if (import.meta.env.DEV && this.baseUrl.includes('not-deployed-yet')) {
      logger.warn('[LambdaCartApiService] Cart API Lambda not deployed yet - API calls will fail until deployment');
    }
  }

  static getInstance(): LambdaCartApiService {
    if (!LambdaCartApiService.instance) {
      LambdaCartApiService.instance = new LambdaCartApiService();
    }
    return LambdaCartApiService.instance;
  }

  /**
   * Generic API call helper with error handling
   */
  private async apiCall<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    try {
      logger.info('[LambdaCartApiService] API call', { endpoint, method: options.method || 'GET' });

      const response = await fetch(url, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API call failed: ${response.status} ${response.statusText} - ${errorText}`);
      }

      const data = await response.json();
      logger.info('[LambdaCartApiService] API call successful', { endpoint, status: response.status });

      return data;
    } catch (error) {
      // Enhanced error handling for development
      if (import.meta.env.DEV && this.baseUrl.includes('not-deployed-yet')) {
        const devError = new Error(
          `Cart API Lambda not deployed yet. Please deploy the Lambda function at /lambda/cart-api/ and update CART_API_BASE_URL environment variable.`
        );
        logger.error('[LambdaCartApiService] Development setup incomplete', { endpoint, originalError: error.message });
        throw devError;
      }

      logger.error('[LambdaCartApiService] API call failed', { endpoint, error: error.message });
      throw error;
    }
  }

  // =====================================================
  // CART SESSIONS
  // =====================================================

  /**
   * Get cart sessions for user
   */
  async getCartSessions(userId: string): Promise<LambdaCartSession[]> {
    try {
      const response = await this.apiCall<{ sessions: LambdaCartSession[] }>(
        `/cart/sessions?userId=${encodeURIComponent(userId)}`
      );

      return response.sessions || [];
    } catch (error) {
      logger.error('[LambdaCartApiService] Failed to get cart sessions', { userId, error: error.message });
      throw error;
    }
  }

  /**
   * Get single cart session
   */
  async getCartSession(sessionId: string): Promise<LambdaCartSession | null> {
    try {
      const response = await this.apiCall<{ session: LambdaCartSession }>(
        `/cart/sessions/${encodeURIComponent(sessionId)}`
      );

      return response.session || null;
    } catch (error) {
      // Return null for 404s (session not found)
      if (error.message.includes('404')) {
        return null;
      }

      logger.error('[LambdaCartApiService] Failed to get cart session', { sessionId, error: error.message });
      throw error;
    }
  }

  /**
   * Create new cart session
   */
  async createCartSession(userId: string): Promise<LambdaCartSession> {
    try {
      const response = await this.apiCall<{ session: LambdaCartSession }>(
        '/cart/sessions',
        {
          method: 'POST',
          body: JSON.stringify({ userId }),
        }
      );

      return response.session;
    } catch (error) {
      logger.error('[LambdaCartApiService] Failed to create cart session', { userId, error: error.message });
      throw error;
    }
  }

  // =====================================================
  // CART ITEMS
  // =====================================================

  /**
   * Get cart items for session
   */
  async getCartItems(sessionId: string): Promise<LambdaCartItem[]> {
    try {
      const response = await this.apiCall<{ items: LambdaCartItem[] }>(
        `/cart/sessions/${encodeURIComponent(sessionId)}/items`
      );

      return response.items || [];
    } catch (error) {
      logger.error('[LambdaCartApiService] Failed to get cart items', { sessionId, error: error.message });
      throw error;
    }
  }

  /**
   * Add item to cart
   */
  async addCartItem(
    sessionId: string,
    item: Omit<LambdaCartItem, 'id' | 'session_id' | 'created_at' | 'updated_at'>
  ): Promise<LambdaCartItem> {
    try {
      const response = await this.apiCall<{ item: LambdaCartItem }>(
        `/cart/sessions/${encodeURIComponent(sessionId)}/items`,
        {
          method: 'POST',
          body: JSON.stringify(item),
        }
      );

      return response.item;
    } catch (error) {
      logger.error('[LambdaCartApiService] Failed to add cart item', { sessionId, error: error.message });
      throw error;
    }
  }

  /**
   * Update cart item quantity
   */
  async updateCartItemQuantity(itemId: string, quantity: number): Promise<void> {
    try {
      await this.apiCall(
        `/cart/items/${encodeURIComponent(itemId)}`,
        {
          method: 'PUT',
          body: JSON.stringify({ quantity }),
        }
      );
    } catch (error) {
      logger.error('[LambdaCartApiService] Failed to update cart item quantity', { itemId, quantity, error: error.message });
      throw error;
    }
  }

  /**
   * Remove cart item
   */
  async removeCartItem(itemId: string): Promise<void> {
    try {
      await this.apiCall(
        `/cart/items/${encodeURIComponent(itemId)}`,
        {
          method: 'DELETE',
        }
      );
    } catch (error) {
      logger.error('[LambdaCartApiService] Failed to remove cart item', { itemId, error: error.message });
      throw error;
    }
  }

  /**
   * Clear all items from cart session
   */
  async clearCartItems(sessionId: string): Promise<void> {
    try {
      await this.apiCall(
        `/cart/sessions/${encodeURIComponent(sessionId)}/items`,
        {
          method: 'DELETE',
        }
      );
    } catch (error) {
      logger.error('[LambdaCartApiService] Failed to clear cart items', { sessionId, error: error.message });
      throw error;
    }
  }

  /**
   * Health check method to verify API connectivity
   */
  async healthCheck(): Promise<boolean> {
    try {
      // Try a simple call to verify the API is accessible
      await fetch(`${this.baseUrl}/health`, { method: 'GET' });
      return true;
    } catch (error) {
      logger.warn('[LambdaCartApiService] Health check failed', { error: error.message });
      return false;
    }
  }
}

// Export singleton instance
export const lambdaCartApiService = LambdaCartApiService.getInstance();